// Tickets — every feedback item + request as a triageable ticket. Filter by
// status / type / severity; click a row to open the detail drawer (full
// description, screenshots, comment thread, status control, and an inline Edit
// mode). The team (owner|admin|dev) can edit, change status, and comment;
// deleting is owner/admin only (UI-gated by role + enforced by RLS). N1: internal only.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Paperclip, MessageSquare, Filter, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import GlassCard from '@/components/shared/GlassCard';
import PageStatsRow from '@/components/shared/PageStatsRow';
import InlineSelect from '@/components/shared/InlineSelect';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import {
  ATTACHMENT_BUCKET,
  FEATURE_AREAS,
  KIND_OPTIONS,
  SEVERITY_OPTIONS,
  STATUS_COLUMNS,
  fetchTickets,
  kindTone,
  severityTone,
  statusTone,
  useTeamRole,
  type Ticket,
  type TicketAttachment,
  type TicketComment,
  type TicketKind,
  type TicketSeverity,
  type TicketStatus,
} from '@/lib/tickets';

const drawerInput =
  'w-full rounded-lg neo-inset px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest/30';

export default function Tickets() {
  const [statusF, setStatusF] = useState<'all' | TicketStatus>('all');
  const [kindF, setKindF] = useState<string>('all');
  const [severityF, setSeverityF] = useState<string>('all');
  const [selected, setSelected] = useState<string | null>(null);

  const ticketsQ = useQuery({ queryKey: ['tickets', 'all'], queryFn: fetchTickets });
  const tickets = ticketsQ.data ?? [];

  const filtered = useMemo(
    () =>
      tickets.filter(
        (t) =>
          (statusF === 'all' || t.status === statusF) &&
          (kindF === 'all' || t.kind === kindF) &&
          (severityF === 'all' || t.severity === severityF),
      ),
    [tickets, statusF, kindF, severityF],
  );

  const stats = useMemo(
    () => [
      { label: 'Open', value: tickets.filter((t) => t.status !== 'done').length },
      { label: 'In progress', value: tickets.filter((t) => t.status === 'in_progress').length },
      {
        label: 'Critical / high',
        value: tickets.filter((t) => t.severity === 'critical' || t.severity === 'high').length,
      },
      { label: 'Done', value: tickets.filter((t) => t.status === 'done').length },
    ],
    [tickets],
  );

  const active = tickets.find((t) => t.id === selected) ?? null;

  return (
    <>
      <PageStatsRow stats={stats} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 my-4">
        <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <InlineSelect
          ariaLabel="Filter by status"
          className="w-40"
          value={statusF}
          onChange={(v) => setStatusF(v as 'all' | TicketStatus)}
          options={[
            { value: 'all', label: 'All statuses' },
            ...STATUS_COLUMNS.map((s) => ({ value: s.value, label: s.label })),
          ]}
        />
        <InlineSelect
          ariaLabel="Filter by type"
          className="w-36"
          value={kindF}
          onChange={setKindF}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'bug', label: 'Bug' },
            { value: 'change_request', label: 'Change' },
            { value: 'feature_request', label: 'Feature' },
          ]}
        />
        <InlineSelect
          ariaLabel="Filter by severity"
          className="w-40"
          value={severityF}
          onChange={setSeverityF}
          options={[
            { value: 'all', label: 'All severities' },
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {tickets.length}
        </span>
      </div>

      {/* List */}
      {ticketsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading tickets…</p>
      ) : ticketsQ.isError ? (
        <GlassCard>
          <p className="text-sm text-alert">Couldn't load tickets. Refresh to try again.</p>
        </GlassCard>
      ) : filtered.length === 0 ? (
        <GlassCard>
          <p className="text-sm text-muted-foreground">
            No tickets match these filters. New feedback shows up here the moment it's submitted on
            the Testing page.
          </p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => (
            <TicketRow key={t.id} ticket={t} onOpen={() => setSelected(t.id)} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <TicketDrawer key={active.id} ticket={active} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </>
  );
}

function TicketRow({ ticket, onOpen }: { ticket: Ticket; onOpen: () => void }) {
  const kt = kindTone(ticket.kind);
  const st = severityTone(ticket.severity);
  const stt = statusTone(ticket.status);
  return (
    <button type="button" onClick={onOpen} className="w-full text-left">
      <GlassCard hover className="!p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground leading-snug truncate">
              {ticket.title}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className={`text-[11px] px-1.5 py-0.5 rounded ${kt.bg} ${kt.text}`}>
                {kt.label}
              </span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>
                {st.label}
              </span>
              {ticket.feature_area && (
                <span className="text-[11px] px-1.5 py-0.5 rounded neo-chip bg-secondary text-muted-foreground">
                  {ticket.feature_area}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${stt.bg} ${stt.text}`}>
              {stt.label}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </GlassCard>
    </button>
  );
}

async function fetchComments(ticketId: string): Promise<TicketComment[]> {
  const { data, error } = await supabase
    .from('ticket_comments')
    .select('id, ticket_id, author_id, body, created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as unknown as TicketComment[]) ?? [];
}

async function fetchAttachments(ticketId: string): Promise<TicketAttachment[]> {
  const { data, error } = await supabase
    .from('ticket_attachments')
    .select('id, ticket_id, storage_path, filename, content_type, size_bytes, created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as unknown as TicketAttachment[]) ?? [];
}

function TicketDrawer({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const qc = useQueryClient();
  const { role } = useTeamRole();
  const canDelete = role === 'owner' || role === 'admin';
  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    title: ticket.title,
    description: ticket.description ?? '',
    feature_area: ticket.feature_area ?? FEATURE_AREAS[0],
    kind: ticket.kind,
    severity: ticket.severity,
  });

  function startEdit(): void {
    setForm({
      title: ticket.title,
      description: ticket.description ?? '',
      feature_area: ticket.feature_area ?? FEATURE_AREAS[0],
      kind: ticket.kind,
      severity: ticket.severity,
    });
    setEditing(true);
  }

  const commentsQ = useQuery({
    queryKey: ['ticket_comments', ticket.id],
    queryFn: () => fetchComments(ticket.id),
  });
  const attachmentsQ = useQuery({
    queryKey: ['ticket_attachments', ticket.id],
    queryFn: () => fetchAttachments(ticket.id),
  });

  const setStatus = useMutation({
    mutationFn: async (status: TicketStatus) => {
      const { error } = await supabase.from('tickets').update({ status }).eq('id', ticket.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not update status.'),
  });

  const addComment = useMutation({
    mutationFn: async () => {
      const body = comment.trim();
      if (!body) throw new Error('Write something first.');
      const { error } = await supabase
        .from('ticket_comments')
        .insert({ ticket_id: ticket.id, body });
      if (error) throw error;
    },
    onSuccess: () => {
      setComment('');
      qc.invalidateQueries({ queryKey: ['ticket_comments', ticket.id] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not add comment.'),
  });

  const saveEdit = useMutation({
    mutationFn: async () => {
      const title = form.title.trim();
      if (!title) throw new Error('Title cannot be empty.');
      const { error } = await supabase
        .from('tickets')
        .update({
          title,
          description: form.description.trim() || null,
          feature_area: form.feature_area,
          kind: form.kind,
          severity: form.severity,
        })
        .eq('id', ticket.id);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditing(false);
      // Patch the cache so the read view reflects the edit immediately — the
      // drawer reads the ticket from this cache, and without this it would show
      // the pre-save values for the refetch window (reads as a silent failure).
      qc.setQueryData<Ticket[]>(['tickets', 'all'], (old) =>
        (old ?? []).map((t) =>
          t.id === ticket.id
            ? {
                ...t,
                title: form.title.trim(),
                description: form.description.trim() || null,
                feature_area: form.feature_area,
                kind: form.kind,
                severity: form.severity,
              }
            : t,
        ),
      );
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket updated.');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save changes.'),
  });

  const del = useMutation({
    mutationFn: async () => {
      // FK cascade removes ticket_comments + ticket_attachments rows; storage
      // objects (screenshots) are left behind — inaccessible without the ticket
      // and rare, so we don't sweep them here.
      // TODO(tickets-orphan-sweep): a storage lifecycle rule / cron could key on
      // ticket_attachments rows absent for a path prefix to reclaim quota.
      const { error } = await supabase.from('tickets').delete().eq('id', ticket.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket deleted.');
      onClose();
    },
    onError: (err) => {
      setConfirmDelete(false);
      toast.error(err instanceof Error ? err.message : 'Could not delete ticket.');
    },
  });

  async function openAttachment(a: TicketAttachment): Promise<void> {
    const { data, error } = await supabase.storage
      .from(ATTACHMENT_BUCKET)
      .createSignedUrl(a.storage_path, 60);
    if (error || !data?.signedUrl) {
      toast.error('Could not open that file.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  const kt = kindTone(ticket.kind);
  const st = severityTone(ticket.severity);

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/30 z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.aside
        className="fixed right-0 top-0 h-screen w-full max-w-[480px] bg-card z-50 overflow-y-auto border-l border-border/60"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
      >
        <div className="sticky top-0 bg-card flex items-center justify-between px-5 py-4 z-10 border-b border-border/60">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Ticket</span>
          <div className="flex items-center gap-1">
            {!editing && (
              <button
                type="button"
                onClick={startEdit}
                aria-label="Edit ticket"
                title="Edit"
                className="p-1.5 rounded-md text-muted-foreground hover:text-forest hover:bg-secondary"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
            {!editing && canDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete ticket"
                title="Delete"
                className="p-1.5 rounded-md text-muted-foreground hover:text-alert hover:bg-alert/5"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {confirmDelete && (
          <div className="px-5 py-3 bg-alert/5 flex items-center justify-between gap-3 border-b border-border/60">
            <span className="text-sm text-foreground">Delete this ticket permanently?</span>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
                disabled={del.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => del.mutate()}
                disabled={del.isPending}
                className="!bg-alert hover:!bg-alert/90"
              >
                {del.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 py-4 space-y-5">
          {editing ? (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Title</span>
                <input
                  className={`${drawerInput} mt-1`}
                  value={form.title}
                  maxLength={200}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">Feature</span>
                  <InlineSelect
                    ariaLabel="Feature area"
                    className="mt-1"
                    value={form.feature_area}
                    onChange={(v) => setForm((f) => ({ ...f, feature_area: v }))}
                    options={FEATURE_AREAS.map((a) => ({ value: a, label: a }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">Type</span>
                  <InlineSelect
                    ariaLabel="Type"
                    className="mt-1"
                    value={form.kind}
                    onChange={(v) => setForm((f) => ({ ...f, kind: v as TicketKind }))}
                    options={KIND_OPTIONS.map((k) => ({ value: k.value, label: k.label }))}
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Severity</span>
                <InlineSelect
                  ariaLabel="Severity"
                  className="mt-1 w-40"
                  value={form.severity}
                  onChange={(v) => setForm((f) => ({ ...f, severity: v as TicketSeverity }))}
                  options={SEVERITY_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Details</span>
                <textarea
                  className={`${drawerInput} mt-1 min-h-[120px] resize-y`}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => saveEdit.mutate()}
                  disabled={saveEdit.isPending || !form.title.trim()}
                >
                  {saveEdit.isPending ? 'Saving…' : 'Save changes'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(false)}
                  disabled={saveEdit.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-lg font-semibold text-foreground leading-snug">{ticket.title}</h2>
              <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${kt.bg} ${kt.text}`}>
                  {kt.label}
                </span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>
                  {st.label} severity
                </span>
                {ticket.feature_area && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded neo-chip bg-secondary text-muted-foreground">
                    {ticket.feature_area}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Opened {format(new Date(ticket.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}

          {/* Status control */}
          <div>
            <span className="text-xs font-medium text-muted-foreground">Status</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {STATUS_COLUMNS.map((s) => {
                const isCurrent = ticket.status === s.value;
                const tone = statusTone(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={setStatus.isPending}
                    onClick={() => !isCurrent && setStatus.mutate(s.value)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-all ${
                      isCurrent
                        ? `${tone.bg} ${tone.text} font-medium ring-1 ring-forest/40`
                        : 'neo-chip bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description (hidden while editing — it's in the edit form above) */}
          {!editing && ticket.description && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Details</span>
              <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap leading-relaxed">
                {ticket.description}
              </p>
            </div>
          )}

          {/* Attachments */}
          {(attachmentsQ.data?.length ?? 0) > 0 && (
            <div>
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Attachments
              </span>
              <div className="mt-1.5 space-y-1.5">
                {attachmentsQ.data!.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => openAttachment(a)}
                    className="w-full text-left text-sm text-forest hover:underline truncate"
                  >
                    {a.filename ?? a.storage_path.split('/').pop()}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div>
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Discussion
            </span>
            <div className="mt-2 space-y-2.5">
              {commentsQ.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : (commentsQ.data?.length ?? 0) === 0 ? (
                <p className="text-xs text-muted-foreground">No comments yet.</p>
              ) : (
                commentsQ.data!.map((c) => (
                  <div key={c.id} className="rounded-lg px-3 py-2 neo-inset">
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">{c.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                    </p>
                  </div>
                ))
              )}
            </div>
            <div className="mt-3">
              <textarea
                className="w-full rounded-lg neo-inset px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest/30 min-h-[68px] resize-y"
                placeholder="Add a note for the team…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  disabled={addComment.isPending || !comment.trim()}
                  onClick={() => addComment.mutate()}
                >
                  {addComment.isPending ? 'Posting…' : 'Comment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
}
